import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import validationSchema from './BidsSearch.schema';
import {
  FormDatePickerField,
  FormInputField,
  FormSelectField,
} from '../../common/components/formFields';
import DealersSelectField from '../../dealers/forms/DealersSelectField';
import LocationsSelectField from '../../locations/forms/LocationsSelectField';
import { getVehicleMakeList, getVehicleModelsList } from '../../vehicles/VehicleActions';
import { dataToSelect } from '../../common/helpers';
import { SET_BIDS_SEARCH_PARAMS, SET_SELECTED_BID } from '../BidsReducer';
import ComponentCard from '../../common/components/cards/ComponentCard';

const initialValues = {
  registrationPlate: '',
  sellerOrganizationId: '',
  bidderOrganizationId: '',
  organizationLocationId: '',
  makeId: '',
  modelId: '',
  year: '',
  bidDateFrom: '',
  bidDateTo: '',
  bidderId: '',
  launchDateFrom: '',
  launchDateTo: '',
  keyword: '',
};

let formContext = null;

function BidsSearch({ onSearch, dealerName }) {
  const previousSearchParams = useSelector((state) => state.bids.get('bidsSearchParams'));
  const dispatch = useDispatch();
  const { dealerId } = useParams();

  const formRef = useRef();
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [reloadDatePickerKey, setReloadDatePickerKey] = useState(false);

  useEffect(() => {
    if (previousSearchParams) {
      formRef.current.setValues(previousSearchParams);
    }
  }, []);

  useEffect(() => {
    if (dealerName && formRef.current) {
      const sellerOption = { value: dealerId, label: dealerName };
      formRef.current.setFieldValue('sellerOrganizationId', sellerOption);
    }
  }, [dealerName, dealerId]);

  useEffect(() => {
    dispatch(
      getVehicleMakeList((makes) => {
        setMakes(dataToSelect(makes.data, ['id', 'name']));
      }),
    );
  }, [dispatch]);

  const handlerMakeChange = (make) => {
    if (make?.value) {
      formContext.setFieldValue('modelId', '');
      dispatch(
        getVehicleModelsList(make.value, (models) => {
          setModels(dataToSelect(models.data, ['id', 'name']));
        }),
      );
    }
  };

  const prepareSearchCriteria = (values, { setSubmitting }) => {
    const params = { ...values };
    if (params?.makeId?.value) {
      params.makeId = params.makeId.value;
    }
    if (params?.modelId?.value) {
      params.modelId = params.modelId.value;
    }
    onSearch(params, setSubmitting);
  };

  return (
    <Row>
      <Col md="12">
        <ComponentCard title="Search Bid" isCollapsible>
          <Formik
            innerRef={formRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={prepareSearchCriteria}
            validateOnBlur
          >
            {(form) => {
              formContext = form;
              return (
                <Form className="search-form-compact">
                  <Row>
                    <Col md="4">
                      <FormInputField name="registrationPlate" label="VRM" />
                    </Col>
                    <Col md="4">
                      <DealersSelectField
                        name="sellerOrganizationId"
                        label="Seller"
                        disabled={!!dealerName}
                      />
                    </Col>
                    <Col md="4">
                      <DealersSelectField
                        name="bidderOrganizationId"
                        label="Buyer"
                        onChangeHandler={() =>
                          form.setFieldValue('organizationLocationId', null)
                        }
                      />
                    </Col>
                    <Col md="4">
                      <LocationsSelectField
                        name="organizationLocationId"
                        label="Buyer Location"
                        organizationId={form.values.bidderOrganizationId?.value || null}
                        disabled={!form.values.bidderOrganizationId?.value}
                      />
                    </Col>
                    <Col md="4">
                      <FormSelectField
                        name="makeId"
                        label="Make"
                        options={makes}
                        onChangeHandler={handlerMakeChange}
                      />
                    </Col>
                    <Col md="4">
                      <FormSelectField name="modelId" label="Model" options={models} />
                    </Col>
                  </Row>
                  <Row>
                    <Col md="4">
                      <FormInputField name="year" label="Year" type="number" />
                    </Col>
                    <Col md="4">
                      <FormDatePickerField
                        name="bidDateFrom"
                        label="Bid Date From"
                        key={reloadDatePickerKey}
                      />
                    </Col>
                    <Col md="4">
                      <FormDatePickerField
                        name="bidDateTo"
                        label="Bid Date To"
                        key={reloadDatePickerKey}
                        isUpperRange
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md="4">
                      <FormDatePickerField
                        name="launchDateFrom"
                        label="Launch Date From"
                        key={reloadDatePickerKey}
                      />
                    </Col>
                    <Col md="4">
                      <FormDatePickerField
                        name="launchDateTo"
                        label="Launch Date To"
                        key={reloadDatePickerKey}
                        isUpperRange
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md="12">
                      <Button
                        className="mt-4 float-end ms-2 rounded-pill btn-primary"
                        type="submit"
                      >
                        Search
                      </Button>
                      <Button
                        className="mt-4 float-end rounded-pill btn-soft-primary"
                        type="button"
                        onClick={() => {
                          dispatch({
                            type: SET_SELECTED_BID,
                          });
                          dispatch({
                            type: SET_BIDS_SEARCH_PARAMS,
                            payload: {
                              status: previousSearchParams?.status,
                            },
                          });
                          setReloadDatePickerKey((prevState) => !prevState);
                          form.resetForm();

                          if (dealerName) {
                            const sellerOption = { value: dealerId, label: dealerName };
                            form.setFieldValue('sellerOrganizationId', sellerOption);
                          }

                          form.submitForm();
                        }}
                      >
                        Clear
                      </Button>
                    </Col>
                  </Row>
                </Form>
              );
            }}
          </Formik>
        </ComponentCard>
      </Col>
    </Row>
  );
}

BidsSearch.propTypes = {
  onSearch: PropTypes.func.isRequired,
  dealerName: PropTypes.string,
};

BidsSearch.defaultProps = {
  dealerName: null,
};

export default BidsSearch;
